/*
 * Copyright (c) 2023 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Thursday, May 11th 2023, 5:14:37 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { APIMany2OneSelectFormInput, APIMany2OneEditButton, ConcreteAPIMany2OneSelectFormInputProps } from "ui-widgets";
import { AIAgentConfiguration } from "lib/schemas";
import { APISERVICEPATHS } from "lib/api";
import { COLORSCHEME } from "theme/styles";
import { RobotIcon } from "theme/icons";
import { StyledOSDialog, useToggleState, FormSubmitBubblingStopper } from "ui-widgets";
import { AIAgentConfigurationForm } from "./AIAgentConfigurationForm";
import { useOSFormContext } from "ui-widgets/forms/providers";

interface AIAgentConfigurationManyToOnePickerProps<TPrimaryObject>
  extends ConcreteAPIMany2OneSelectFormInputProps<TPrimaryObject, AIAgentConfiguration> {}

export const AIAgentConfigurationManyToOnePicker = <TPrimaryObject,>(
  props: AIAgentConfigurationManyToOnePickerProps<TPrimaryObject>
) => {
  const { aiAgentConfigurationId, editIsOpen, handleCloseEdit, handleOpenEdit } = useAIAgentConfigurationMany2OnePicker(
    props.schema_field_key
  );

  return (
    <>
      <APIMany2OneSelectFormInput<TPrimaryObject, AIAgentConfiguration>
        label={props.label ?? "AI Agent Configuration"}
        itemIdProperty="id"
        getOptionLabel={(option) => option.name}
        loadingText="Loading Configuration"
        apiServicePath={APISERVICEPATHS.aiAgentConfigurations}
        inputStartAdornment={<RobotIcon fill={COLORSCHEME.primary} width={30} height={30} />}
        inputEndAdornment={<APIMany2OneEditButton objectId={aiAgentConfigurationId} onClick={handleOpenEdit} />}
        renderRelatedObjectForm={({ updatePrimaryObject, closeNewFormFn, newFormOpenState }) => (
          <StyledOSDialog
            open={newFormOpenState}
            onClose={closeNewFormFn}
            popUpTitle="New AI Agent Configuration"
            popUpIcon={<RobotIcon width={30} height={30} fill={COLORSCHEME.primary} />}
          >
            <FormSubmitBubblingStopper>
              <AIAgentConfigurationForm
                updateAIAgentConfigurationFromAPICallback={(aiAgentConfiguration) =>
                  updatePrimaryObject(aiAgentConfiguration.id)
                }
                aiAgentConfigurationDefaultValues={null}
                aiAgentConfigurationFetchId={null}
              />
            </FormSubmitBubblingStopper>
          </StyledOSDialog>
        )}
        {...props}
      />

      <StyledOSDialog
        open={editIsOpen}
        onClose={handleCloseEdit}
        popUpTitle="Edit AI Agent Configuration"
        popUpIcon={<RobotIcon width={30} height={30} fill={COLORSCHEME.primary} />}
      >
        <FormSubmitBubblingStopper>
          <AIAgentConfigurationForm
            updateAIAgentConfigurationFromAPICallback={(aiAgentConfiguration) => {}}
            aiAgentConfigurationDefaultValues={null}
            aiAgentConfigurationFetchId={aiAgentConfigurationId}
          />
        </FormSubmitBubblingStopper>
      </StyledOSDialog>
    </>
  );
};

const useAIAgentConfigurationMany2OnePicker = <TPrimaryObject,>(
  schema_field_key: ConcreteAPIMany2OneSelectFormInputProps<TPrimaryObject, AIAgentConfiguration>["schema_field_key"]
) => {
  const { watch } = useOSFormContext<TPrimaryObject>();

  const aiAgentConfigurationId = watch<any>(schema_field_key) as AIAgentConfiguration["id"];

  const { handleClose: handleCloseEdit, handleOpen: handleOpenEdit, open: editIsOpen } = useToggleState();

  return { aiAgentConfigurationId, handleCloseEdit, handleOpenEdit, editIsOpen };
};
